import { expVal, expValEquals } from '@confluence/feature-experiments';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { preloadSpaceViews } from '@confluence/space-views/entry-points/preloadSpaceViews.ts';
import { preloadShortcutsSmartLinks } from '@confluence/space-shortcuts/entry-points/preloadShortcutsSmartLinks.ts';
import { preloadSideNavigation } from '@confluence/side-navigation/entry-points/preloadSideNavigation.ts';
import { preloadSpaceGuestList } from '@confluence/space-guest-list/entry-points/preloadSpaceGuestList.ts';
import { preloadContentTypesHeader } from '@confluence/content-types-header/entry-points/preloadContentTypesHeader.js';
import { preloadPageRestrictionsQuery } from '@confluence/page-restrictions-context/entry-points/preloadPageRestrictionsQuery.ts';
import { preloadWhiteboardData } from '@confluence/whiteboard-common/entry-points/preloadWhiteboardData.ts';
import { preloadShareAndRestrictButtonQuery } from '@confluence/share-and-restrict-dialog/entry-points/preloadShareAndRestrictButtonQuery.ts';
import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import { createDataFetcherForSuspense } from '@confluence/query-preloader-tools/entry-points/dataFetcherForSuspense.ts';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';
import type { RouteMatch } from '@confluence/route/entry-points/Route.ts';
import { fg } from '@confluence/feature-gating';
import { preloadSmartCreateFeedbackBanner } from '@confluence/editor-action-bar/entry-points/preloadSmartCreateFeedbackBanner.ts';

import { getEmbeddedWhiteboardContext } from './getEmbeddedWhiteboardContext.ts';

export const preloadWhiteboardRoute = async (
	match: RouteMatch,
	_url: string,
	isTransition: boolean = false,
): Promise<void | any[] | Error[]> => {
	const { isLicensed } = getPreloaderFnContext();
	const { bypassesMainLayout, isSmartLinkEmbed, isRovoEmbed } = getEmbeddedWhiteboardContext(match);
	const tasks: Promise<any>[] = [];
	const { spaceKey, contentId } = match.params;

	if (!bypassesMainLayout) {
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (process.env.REACT_SSR) {
			// Only load data for navigation in React SSR since we are delaying them in the SPA
			const preloadNavigation = () => [
				preloadSideNavigation(spaceKey),
				!StatsigConfigurations.isKillSwitchOn('confluence_space_nav_affordances_frag_reader') &&
				expVal('confluence_exp_space_nav_contract', 'isEnabled', false)
					? Promise.resolve()
					: preloadSpaceGuestList(spaceKey),
				preloadShortcutsSmartLinks(spaceKey),
			];
			if (expValEquals('confluence_react_streaming_all_routes', 'isEnabled', true)) {
				tasks.push(createDataFetcherForSuspense('side-navigation', preloadNavigation));
			} else {
				tasks.push(...preloadNavigation());
			}
		} else if (!isTransition) {
			tasks.push(preloadSpaceViews(spaceKey, isLicensed));
		}
	}

	// Content types header (object header)
	if (!(bypassesMainLayout && isSmartLinkEmbed)) {
		tasks.push(preloadContentTypesHeader(contentId));
		// Standalone Restrictions button needs to have its data preloaded)
		if (!(bypassesMainLayout && isRovoEmbed)) {
			tasks.push(preloadPageRestrictionsQuery(contentId, false));
		}
	}

	// Unified Share and Restrict Button
	if (
		!(bypassesMainLayout && (isSmartLinkEmbed || isRovoEmbed)) &&
		// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
		fg('confluence_frontend_usd_ssr')
	) {
		tasks.push(preloadShareAndRestrictButtonQuery(contentId));
	}

	if (!(bypassesMainLayout && isRovoEmbed)) {
		tasks.push(preloadSmartCreateFeedbackBanner(contentId));
	}

	tasks.push(
		preloadWhiteboardData({
			spaceKey,
			contentId,
			skipPageTree: bypassesMainLayout,
		}),
	);

	return waitForAllReturnWhenError(tasks);
};
